import PropTypes from "prop-types";
import React from "react";
import "../../scss/delivery-note-documentation.scss";
import { docLeaves, formatDocCellForDisplay, getDocFieldWidget, splitDocColumns } from "./utils";

const EMPTY_VALUE = "—";

function DocLabel({ col }) {
  const showUnit = !!col["x-unit"] && getDocFieldWidget(col) !== "number";
  return (
    <>
      {col.title}
      {showUnit && <small className="text-muted ms-1">({col["x-unit"]})</small>}
    </>
  );
}

function DocValue({ col, value }) {
  const text = formatDocCellForDisplay(col, value);
  if (!text) return <span className="text-muted">{EMPTY_VALUE}</span>;
  return text;
}

function SummaryField({ col, row }) {
  return (
    <div>
      <div className="small text-muted">
        <DocLabel col={col} />
      </div>
      <div className="fw-bold">
        <DocValue col={col} value={row[col.id]} />
      </div>
    </div>
  );
}

function GroupFields({ group, row }) {
  return (
    <div className="border-start border-3 ps-3">
      <div className="text-uppercase small text-muted fw-semibold mb-1">{group.title}</div>
      <dl className="mb-0">
        {docLeaves([group]).map((leaf) => (
          <div key={leaf.id} className="d-flex justify-content-between gap-2">
            <dt className="fw-normal text-muted">
              <DocLabel col={leaf} />
            </dt>
            <dd className="mb-0 fw-semibold text-end">
              <DocValue col={leaf} value={row[leaf.id]} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function DeliveryNoteDetailsDocRecords({ columns, rows, rowLabel }) {
  const { leading, groups, trailing } = splitDocColumns(columns);

  return (
    <div className="d-flex flex-column gap-3">
      {rows.map((row, rowIndex) => (
        <div key={`doc-record-${rowIndex}`} className="border rounded p-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <span className="badge bg-secondary">
              {rowLabel} {rowIndex + 1}
            </span>
            {leading.map((col) => (
              <SummaryField key={col.id} col={col} row={row} />
            ))}
          </div>
          <div className="dn-doc-columns">
            {groups.map((group, groupIndex) => (
              <GroupFields key={`${group.title}-${groupIndex}`} group={group} row={row} />
            ))}
          </div>
          {trailing.length > 0 && (
            <div className="d-flex flex-wrap gap-3 border-top mt-3 pt-3">
              {trailing.map((col) => (
                <SummaryField key={col.id} col={col} row={row} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const columnShape = PropTypes.shape({
  kind: PropTypes.oneOf(["field", "group"]).isRequired,
  id: PropTypes.string,
  title: PropTypes.string,
});

DocLabel.propTypes = { col: columnShape.isRequired };
DocValue.propTypes = { col: columnShape.isRequired, value: PropTypes.any };
SummaryField.propTypes = { col: columnShape.isRequired, row: PropTypes.object.isRequired };
GroupFields.propTypes = { group: columnShape.isRequired, row: PropTypes.object.isRequired };

DeliveryNoteDetailsDocRecords.propTypes = {
  columns: PropTypes.arrayOf(columnShape).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowLabel: PropTypes.string.isRequired,
};
